import {
  ToolbarItem,
  SideNavGroup,
  SideNavLink,
} from '@amzn/aac-chrome-internal-api';
import React from 'react';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { Flex, Icon, Text } from '@amzn/storm-ui';
import {
  AllToolsTitleWrapper,
  AccordionLinkWrapper,
  AccordionContentGroupWrapper,
  AccordionContentInnerWrapper,
  AccordionContentSubTitle,
  NestedGroupIndent,
  StyledDivider as Divider,
} from './styles';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { renderFlag } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { externalLink } from '@amzn/storm-ui-icons';
import {
  AccordionWrapper,
  AccordionLink,
  DATA_HAS_NEXT_GROUP,
  DATA_CONTINUATION_BLOCK,
} from '../styles';
import { useInterstitialModalIntercept } from '../InterstitialModal/useInterstitialModalIntercept';

interface AllToolsToolbarItemProps {
  toolbarItem: ToolbarItem;
}

const AllToolsToolbarItem: React.VFC<AllToolsToolbarItemProps> = ({
  toolbarItem,
}) => {
  const id = toolbarItem.id;
  const bundle = useSidenavBundle();

  const { isNavOpen, activeCategoryId } = useConsolidatedNavContext();
  const tryInterceptInterstitial = useInterstitialModalIntercept();

  if (!toolbarItem) {
    return null;
  }

  const isActive = id === activeCategoryId;
  const iconPath = toolbarItem.iconPath;
  const title = toolbarItem.toolTip;

  const ariaLabel = bundle
    .getMessage('sidenav-show-links-aria-label')
    .replace('{title}', title ?? '');

  return (
    <>
      {toolbarItem.sidebarGroups.map((group: SideNavGroup, index: number) => {
        const shouldFlattenSidebarGroupWithSingleLink =
          !!group.collapsible && group.links.length === 1;
        const shouldShowGroupTitle =
          !!group.title && !shouldFlattenSidebarGroupWithSingleLink;
        return (
          <AccordionWrapper
            isActive={isActive && index === 0}
            key={`${id}-group-${index}`}
            {...(index < toolbarItem.sidebarGroups.length - 1 && {
              [DATA_HAS_NEXT_GROUP]: 'true',
            })}
            {...(index > 0 && { [DATA_CONTINUATION_BLOCK]: 'true' })}
          >
            {index === 0 && (
              <AllToolsTitleWrapper
                gap="xxsmall"
                isSelected={false}
                isNavOpen={isNavOpen}
                data-ccx-e2e-id={id}
                aria-label={ariaLabel}
                aria-expanded={true}
                {...getTaktAttributes(id, chromeComponents.ALL_TOOLS_NAV)}
              >
                <SvgIcon
                  iconPath={iconPath}
                  ariaLabel={title}
                  isSelected={false}
                />
                <Text renderAs="span">{title}</Text>
              </AllToolsTitleWrapper>
            )}
            <AccordionContentInnerWrapper>
              <AccordionContentGroupWrapper
                key={`${index}-${group.title}`}
                direction="column"
                rowGap="xxxsmall"
              >
                {shouldShowGroupTitle && (
                  <AccordionContentSubTitle>
                    {group.title}
                  </AccordionContentSubTitle>
                )}
                <NestedGroupIndent $show={shouldShowGroupTitle}>
                  {group.links.map((sideNavLink: SideNavLink) => {
                    const isExternalLink: boolean =
                      sideNavLink.sidebarItemType === 'EXTERNAL_LINK';
                    const { link, appName, calloutTag, id } = sideNavLink;
                    return (
                      <AccordionLinkWrapper
                        key={id}
                        isSelected={false}
                        data-ccx-e2e-id={`${id}-accordion-link-wrapper`}
                      >
                        <AccordionLink
                          isSelected={false}
                          href={link.url}
                          target={isExternalLink ? '_blank' : '_self'}
                          data-ccx-e2e-id={id}
                          aria-label={link.label}
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                            tryInterceptInterstitial(link.url, appName, e)
                          }
                          {...getTaktAttributes(
                            id,
                            chromeComponents.ALL_TOOLS_NAV,
                            {
                              url: link.url,
                            },
                          )}
                        >
                          {link.label}
                        </AccordionLink>
                        <Flex
                          alignItems="center"
                          data-ccx-e2e-id={`sidenav-link-features-${appName}`}
                        >
                          {!!calloutTag && renderFlag(sideNavLink)}
                          {isExternalLink && (
                            <Icon type={externalLink} size="small" />
                          )}
                        </Flex>
                      </AccordionLinkWrapper>
                    );
                  })}
                </NestedGroupIndent>
                {index !== toolbarItem.sidebarGroups.length - 1 && <Divider />}
              </AccordionContentGroupWrapper>
            </AccordionContentInnerWrapper>
          </AccordionWrapper>
        );
      })}
    </>
  );
};

export default AllToolsToolbarItem;
